import type {
  BetterplaceTemplateVariableKey,
  CrossSettings,
} from '@/services/betterplaceService'
import type { TemplateComponent, WhatsAppTemplate } from '@/services/whatsappService'

export type BetterplaceTemplateRole = 'buyers' | 'coordination'

export type BetterplaceTemplateVariableSpec = {
  key: BetterplaceTemplateVariableKey
  label: string
  example: string
}

type RoleFieldNames = {
  name: keyof CrossSettings
  language: keyof CrossSettings
  variables: keyof CrossSettings
}

export type BetterplaceTemplateAnalysis = {
  compatible: boolean
  reason: string | null
  bodyVariableCount: number
  bodyText: string
  status: string
}

const PLACEHOLDER_RE = /\{\{(\d+)\}\}/g

const ROLE_FIELDS: Record<BetterplaceTemplateRole, RoleFieldNames> = {
  buyers: {
    name: 'template_buyers',
    language: 'template_buyers_language',
    variables: 'template_buyers_variables',
  },
  coordination: {
    name: 'template_coordination',
    language: 'template_coordination_language',
    variables: 'template_coordination_variables',
  },
}

const BUYER_VARIABLE_SPECS: BetterplaceTemplateVariableSpec[] = [
  { key: 'buyer_name', label: 'Nombre del comprador', example: 'Ana Compradora' },
  { key: 'buyer_phone', label: 'Teléfono del comprador', example: '+34600111222' },
  { key: 'property_label', label: 'Propiedad', example: 'Piso centro' },
  { key: 'property_url', label: 'URL de la propiedad', example: 'https://example.com/property/2064440' },
  { key: 'property_price', label: 'Precio de la propiedad', example: '350000' },
  { key: 'property_ref', label: 'Referencia', example: '2064440' },
]

const COORDINATION_VARIABLE_SPECS: BetterplaceTemplateVariableSpec[] = [
  { key: 'buyer_name', label: 'Nombre del comprador', example: 'Ana Compradora' },
  { key: 'buyer_phone', label: 'Teléfono del comprador', example: '+34600111222' },
  { key: 'property_label', label: 'Propiedad', example: 'Piso centro' },
  { key: 'property_url', label: 'URL de la propiedad', example: 'https://example.com/property/2064440' },
  { key: 'property_price', label: 'Precio de la propiedad', example: '350000' },
  { key: 'property_ref', label: 'Referencia', example: '2064440' },
  { key: 'cross_id', label: 'ID del cruce', example: 'cross-123' },
]

function extractBodyText(components: TemplateComponent[]): string {
  const body = components.find((component) => component.type?.toUpperCase() === 'BODY')
  return body?.text?.trim() ?? ''
}

function componentContainsPlaceholders(value: unknown): boolean {
  if (typeof value === 'string') {
    return /\{\{\d+\}\}/.test(value)
  }
  if (Array.isArray(value)) {
    return value.some((item) => componentContainsPlaceholders(item))
  }
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((item) => componentContainsPlaceholders(item))
  }
  return false
}

function analyzeBodyPlaceholders(bodyText: string): Pick<BetterplaceTemplateAnalysis, 'compatible' | 'reason' | 'bodyVariableCount'> {
  if (!bodyText) {
    return { compatible: true, reason: null, bodyVariableCount: 0 }
  }

  const matches = Array.from(bodyText.matchAll(PLACEHOLDER_RE)).map((match) => Number.parseInt(match[1], 10))
  if (matches.length === 0) {
    return { compatible: true, reason: null, bodyVariableCount: 0 }
  }

  const distinct = Array.from(new Set(matches)).sort((left, right) => left - right)
  const maxIndex = distinct[distinct.length - 1] ?? 0
  const expected = Array.from({ length: maxIndex }, (_, index) => index + 1)
  const compatible = expected.length === distinct.length && expected.every((value, index) => distinct[index] === value)

  if (!compatible) {
    return {
      compatible: false,
      reason: 'La plantilla debe usar variables consecutivas en BODY desde {{1}}.',
      bodyVariableCount: 0,
    }
  }

  return { compatible: true, reason: null, bodyVariableCount: maxIndex }
}

export function analyzeBetterplaceTemplate(template: WhatsAppTemplate | undefined): BetterplaceTemplateAnalysis {
  if (!template) {
    return {
      compatible: false,
      reason: 'No se encontró metadata de la plantilla en la cuenta conectada.',
      bodyVariableCount: 0,
      bodyText: '',
      status: 'UNKNOWN',
    }
  }

  const status = template.status?.toUpperCase() || 'UNKNOWN'
  if (status !== 'APPROVED') {
    return {
      compatible: false,
      reason: 'La plantilla debe estar aprobada para usarse en Betterplace.',
      bodyVariableCount: 0,
      bodyText: extractBodyText(template.components),
      status,
    }
  }

  const bodyText = extractBodyText(template.components)
  if (!bodyText) {
    return {
      compatible: false,
      reason: 'La plantilla debe incluir un BODY para mapear variables.',
      bodyVariableCount: 0,
      bodyText,
      status,
    }
  }

  const hasParameterizedHeader = template.components.some((component) => {
    return component.type?.toUpperCase() === 'HEADER' && componentContainsPlaceholders(component)
  })
  if (hasParameterizedHeader) {
    return {
      compatible: false,
      reason: 'Los headers parametrizados no están soportados en Betterplace V1.',
      bodyVariableCount: 0,
      bodyText,
      status,
    }
  }

  const hasParameterizedButtons = template.components.some((component) => {
    const type = component.type?.toUpperCase()
    return (type === 'BUTTON' || type === 'BUTTONS') && componentContainsPlaceholders(component)
  })
  if (hasParameterizedButtons) {
    return {
      compatible: false,
      reason: 'Los botones parametrizados no están soportados en Betterplace V1.',
      bodyVariableCount: 0,
      bodyText,
      status,
    }
  }

  const bodyAnalysis = analyzeBodyPlaceholders(bodyText)
  return {
    compatible: bodyAnalysis.compatible,
    reason: bodyAnalysis.reason,
    bodyVariableCount: bodyAnalysis.bodyVariableCount,
    bodyText,
    status,
  }
}

export function getBetterplaceVariableSpecs(role: BetterplaceTemplateRole): BetterplaceTemplateVariableSpec[] {
  return role === 'buyers' ? BUYER_VARIABLE_SPECS : COORDINATION_VARIABLE_SPECS
}

export function getBetterplaceTemplateFieldNames(role: BetterplaceTemplateRole): RoleFieldNames {
  return ROLE_FIELDS[role]
}

export type BetterplaceTemplateSelectOption = {
  value: string
  label: string
}

export function buildBetterplaceTemplateOptions(
  templates: WhatsAppTemplate[],
): BetterplaceTemplateSelectOption[] {
  return templates
    .filter((template) => (template.status?.toUpperCase() ?? '') === 'APPROVED')
    .map((template) => {
      const analysis = analyzeBetterplaceTemplate(template)
      const variableSuffix =
        analysis.compatible && analysis.bodyVariableCount > 0
          ? ` — ${analysis.bodyVariableCount} vars`
          : analysis.compatible
            ? ' — sin vars'
            : ''
      return {
        value: `${template.name}:${template.language}`,
        label: `${template.name} (${template.language})${variableSuffix}`,
      }
    })
}

export function previewBetterplaceTemplateBody(
  bodyText: string,
  variableKeys: BetterplaceTemplateVariableKey[],
  role: BetterplaceTemplateRole,
): string {
  const specs = getBetterplaceVariableSpecs(role)
  return bodyText.replace(/\{\{(\d+)\}\}/g, (match, rawIndex) => {
    const index = Number.parseInt(rawIndex, 10) - 1
    const key = variableKeys[index]
    if (!key) return match
    const spec = specs.find((item) => item.key === key)
    return spec?.example ?? match
  })
}

export function findBetterplaceTemplate(
  templates: WhatsAppTemplate[],
  templateName?: string,
  languageCode?: string,
): WhatsAppTemplate | undefined {
  const normalizedName = templateName?.trim()
  if (!normalizedName) {
    return undefined
  }

  const normalizedLanguage = languageCode?.trim()
  if (normalizedLanguage) {
    const exactMatch = templates.find(
      (template) => template.name === normalizedName && template.language === normalizedLanguage,
    )
    if (exactMatch) {
      return exactMatch
    }
  }

  return templates.find((template) => template.name === normalizedName)
}

export function getDefaultBetterplaceVariableKeys(
  role: BetterplaceTemplateRole,
  count: number,
): BetterplaceTemplateVariableKey[] {
  return getBetterplaceVariableSpecs(role)
    .slice(0, Math.max(0, count))
    .map((spec) => spec.key)
}

export function getEffectiveBetterplaceVariableKeys(
  settings: CrossSettings,
  role: BetterplaceTemplateRole,
  template: WhatsAppTemplate | undefined,
): BetterplaceTemplateVariableKey[] {
  const analysis = analyzeBetterplaceTemplate(template)
  if (!analysis.compatible) {
    return []
  }

  const { variables } = getBetterplaceTemplateFieldNames(role)
  const stored = Array.isArray(settings[variables])
    ? (settings[variables] as BetterplaceTemplateVariableKey[])
    : []

  const defaults = getDefaultBetterplaceVariableKeys(role, analysis.bodyVariableCount)
  return Array.from({ length: analysis.bodyVariableCount }, (_, index) => {
    return stored[index] || defaults[index]
  })
}

export function buildNormalizedBetterplaceCrossSettings(
  settings: CrossSettings,
  templates: WhatsAppTemplate[],
): { settings: CrossSettings; errors: string[] } {
  const nextSettings: CrossSettings = { ...settings }
  const errors: string[] = []

  ;(['buyers', 'coordination'] as BetterplaceTemplateRole[]).forEach((role) => {
    const { name, language, variables } = getBetterplaceTemplateFieldNames(role)
    const templateName = typeof nextSettings[name] === 'string' ? nextSettings[name]?.trim() : ''

    if (!templateName) {
      nextSettings[name] = undefined
      nextSettings[language] = undefined
      nextSettings[variables] = undefined
      return
    }

    const template = findBetterplaceTemplate(
      templates,
      typeof nextSettings[name] === 'string' ? nextSettings[name] : undefined,
      typeof nextSettings[language] === 'string' ? nextSettings[language] : undefined,
    )

    if (!template) {
      errors.push(
        role === 'buyers'
          ? 'Plantilla contacto: elige una plantilla aprobada en la cuenta de WhatsApp conectada.'
          : 'Plantilla empleados: elige una plantilla aprobada en la cuenta de WhatsApp conectada.',
      )
      return
    }

    const analysis = analyzeBetterplaceTemplate(template)
    if (!analysis.compatible) {
      errors.push(
        role === 'buyers'
          ? `Plantilla contacto: ${analysis.reason}`
          : `Plantilla empleados: ${analysis.reason}`,
      )
      return
    }

    // `name`, `language` y `variables` salen correlacionados de
    // getBetterplaceTemplateFieldNames(role), pero TypeScript no puede seguir
    // esa correlacion: al indexar con una union de claves resuelve el tipo del
    // valor como la INTERSECCION de todos los posibles -string &
    // BetterplaceTemplateVariableKey[]-, que no satisface ningun valor real.
    // Se relaja solo la escritura por clave dinamica; los tres valores siguen
    // comprobados por sus propias funciones.
    const write = nextSettings as Record<string, unknown>
    write[name] = template.name
    write[language] = template.language
    write[variables] = getEffectiveBetterplaceVariableKeys(nextSettings, role, template)
  })

  const coordinationTemplate = String(nextSettings.template_coordination || '').trim()
  const coordinationPhone = String(nextSettings.coordination_phone || '').trim()
  if (coordinationTemplate && !coordinationPhone) {
    errors.push('Plantilla empleados: indica el número de WhatsApp de destino del equipo.')
  }

  return { settings: nextSettings, errors }
}